/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* Licensed under the MIT License. See LICENSE.md in the project root for license terms.
*--------------------------------------------------------------------------------------------*/

import { editor } from "monaco-editor/esm/vs/editor/editor.api";
import { IAnnotationData } from "../../types/Annotation";
import { StyleManager } from "./StyleManager";
import "./AnnotationTypes.css";

export const GlyphNumber = (id: string, title?: string): editor.IModelDecorationOptions => {
  const idString = id.toString().replace(/\.|\#/, "-");
  const className = `.annotation-number-${idString}`
  StyleManager.createCSSSelector(`${className}:after`, {
    content: `"${id}"`
  })

  return {
    glyphMarginClassName: `annotation-number ${className}`,
    glyphMarginHoverMessage: { value: title ?? "" },
    minimap: {
      color: "var(--iui-color-background-accent)",
      position: editor.MinimapPosition.Gutter
    },
  }
}

export const GlyphAnnotation: editor.IModelDecorationOptions = {
  glyphMarginClassName: "annotation-asterisk",
  minimap: {
    color: "var(--iui-color-background-accent)",
    position: editor.MinimapPosition.Gutter
  },
}

export const BlurAnnotation: editor.IModelDecorationOptions = {
  className: "annotation-blurred",
  isWholeLine: true,
};

export const DimmedAnnotation: editor.IModelDecorationOptions = {
  className: "annotation-dimmed",
  isWholeLine: true,
};

export const HighlightAnnotation: editor.IModelDecorationOptions = {
  className: "annotation-inline",
  isWholeLine: true,
  minimap: {
    color: "var(--iui-color-background-accent)",
    position: editor.MinimapPosition.Inline
  }
}


export function createBlurAnnotation(startLineNumber: number, endLineNumber: number): IAnnotationData {
  return {
    options: BlurAnnotation,
    lineNumber: startLineNumber,
    endLineNumber: endLineNumber,
  }
}

export function createDimmedAnnotation(startLineNumber: number, endLineNumber: number): IAnnotationData {
  return {
    options: DimmedAnnotation,
    lineNumber: startLineNumber,
    endLineNumber: endLineNumber,
  }
}

export function createHighlightAnnotation(startLineNumber: number, endLineNumber: number): IAnnotationData {
  return {
    options: HighlightAnnotation,
    lineNumber: startLineNumber,
    endLineNumber: endLineNumber,
  }
}

export function createGlyphNumber(id: string, lineNumber: number, title?: string): IAnnotationData {
  return {
    options: GlyphNumber(id, title),
    lineNumber: lineNumber,
    endLineNumber: lineNumber,
  }
}

export function createGlyphAnnotation(lineNumber: number): IAnnotationData {
  return {
    options: GlyphAnnotation,
    lineNumber: lineNumber,
    endLineNumber: lineNumber,
  }
}